import React, { useState, useEffect } from 'react';
import { notifyCartUpdated } from '../../utils/cartEvents';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Package, MapPin, FileText, CreditCard, ShoppingCart, Trash2, Edit3, AlertCircle, Loader, ArrowRight, Plus, CheckCircle2, X } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import UserLayout from './UserLayout';
import { showNotification, showError } from '../../utils/notificationHandler';
import { useOrderingEnabled, fetchOrderingSettings } from '../../hooks/useOrderingEnabled';

const ProcessingOverlay = ({ steps, currentStep }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4"
    >
      <div className="text-center mb-6">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="inline-block"
        >
          <Loader className="w-12 h-12 text-brand" />
        </motion.div>
        <h3 className="text-xl font-bold text-gray-900 mt-4">Processing Your Order</h3>
        <p className="text-sm text-gray-600 mt-1">Please wait while we process your order...</p>
      </div>

      <div className="space-y-3">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isPending = index > currentStep;

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex items-center space-x-3 p-3 rounded-lg ${
                isCurrent ? 'bg-brand/10 border border-brand' : 
                isCompleted ? 'bg-green-50 border border-green-200' : 
                'bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex-shrink-0">
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : isCurrent ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Loader className="w-5 h-5 text-brand" />
                  </motion.div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  isCurrent ? 'text-brand-dark' : 
                  isCompleted ? 'text-green-700' : 
                  'text-gray-500'
                }`}>
                  {step}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-6">
        <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="absolute top-0 left-0 h-full bg-brand"
            initial={{ width: '0%' }}
            animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <p className="text-xs text-center text-gray-500 mt-2">
          Step {currentStep + 1} of {steps.length}
        </p>
      </div>
    </motion.div>
  </div>
);

const OrderSummaryItem = ({ item, index, onUpdateComment, onUpdateQuantity, onRemoveItem }) => {
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [comment, setComment] = useState(item.comment || '');

  const handleSaveComment = () => {
    onUpdateComment(index, comment);
    setIsEditingComment(false);
  };

  const handleCancelComment = () => {
    setComment(item.comment || '');
    setIsEditingComment(false);
  };

  const itemTotal = ((parseFloat(item.price) || 0) * item.quantity).toFixed(2);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-4 lg:space-y-0">
        {/* Product Info */}
        <div className="flex items-start space-x-4 flex-1">
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="w-8 h-8 text-gray-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{item.partNumber}</h3>
            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
            {item.isBOM && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                BOM Item
              </span>
            )}
          </div>
        </div>

        {/* Quantity and Price */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
          <div className="text-center">
            <p className="text-sm text-gray-600">Quantity</p>
            <p className="text-lg font-semibold text-gray-900">{item.quantity}</p>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">Unit Price</p>
            <p className="text-lg font-semibold text-gray-900">
              {item.price && parseFloat(item.price) > 0 ? `$${parseFloat(item.price).toFixed(2)}` : 'POA'}
            </p>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-xl font-bold text-green-600">${itemTotal}</p>
          </div>

          <button
            onClick={() => onRemoveItem(index)}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
            title="Remove from order"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Comments Section */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Item Instructions</label>
          {!isEditingComment && (
            <button
              onClick={() => setIsEditingComment(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              <Edit3 size={14} className="mr-1" />
              {item.comment ? 'Edit' : 'Add'}
            </button>
          )}
        </div>

        {isEditingComment ? (
          <div className="space-y-2">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add any special instructions for this item..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
              rows="3"
            />
            <div className="flex space-x-2">
              <button
                onClick={handleSaveComment}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors duration-200"
              >
                Save
              </button>
              <button
                onClick={handleCancelComment}
                className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-600">
            {item.comment ? (
              <p className="bg-gray-50 p-3 rounded-lg">{item.comment}</p>
            ) : (
              <p className="text-gray-400 italic">No special instructions</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AddressCard = ({ address, isSelected, onSelect }) => (
  <div 
    className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
      isSelected 
        ? 'border-brand bg-brand/5 shadow-md' 
        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
    }`}
    onClick={() => onSelect(address.id)}
  >
    <div className="flex items-start space-x-3">
      <div className={`mt-1 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
        isSelected ? 'border-brand bg-brand' : 'border-gray-300'
      }`}>
        {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
      </div>
      <div className="flex-1">
        <div className="flex items-center space-x-2">
          <MapPin className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-gray-900">{address.address_line1}</span>
          {address.is_primary && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Primary</span>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {address.address_line2 && `${address.address_line2}, `}
          {address.city}, {address.state} {address.postal_code}
        </p>
      </div>
    </div>
  </div>
);

// Parse a manually-typed address string into structured fields. Mirrors the
// parser used in UserSettings so both entry points behave identically.
const parseManualAddress = (addressText) => {
  if (!addressText || addressText.trim() === '') {
    throw new Error('Please enter an address');
  }

  const parts = addressText.split(',').map(p => p.trim());

  if (parts.length < 2) {
    throw new Error('Please enter address in format: Street Address, City, State Postcode');
  }

  const addressLine1 = parts[0];
  let city = '';
  let state = '';
  let postalCode = '';

  const lastPart = parts[parts.length - 1].trim();
  const statePostcodeMatch = lastPart.match(/([A-Z]{2,3})\s+(\d{4})/);

  if (statePostcodeMatch) {
    state = statePostcodeMatch[1];
    postalCode = statePostcodeMatch[2];
    if (parts.length > 2) {
      city = parts[parts.length - 2].trim();
    } else {
      city = lastPart.substring(0, statePostcodeMatch.index).trim();
    }
  } else if (parts.length >= 2) {
    city = parts[1].trim();
    const cityStateMatch = city.match(/^(.+?)\s+([A-Z]{2,3})\s+(\d{4})$/);
    if (cityStateMatch) {
      city = cityStateMatch[1];
      state = cityStateMatch[2];
      postalCode = cityStateMatch[3];
    }
  }

  if (!postalCode) {
    throw new Error('Could not extract postal code. Please ensure format: Street Address, City, State Postcode (e.g., "123 Main St, Perth, WA 6000")');
  }

  return {
    address_line1: addressLine1,
    city: city || 'Not specified',
    state: state || 'WA',
    postal_code: postalCode
  };
};

const AddAddressModal = ({ onClose, onAdded }) => {
  const [addressInput, setAddressInput] = useState('');
  const [structuredAddress, setStructuredAddress] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load Google Maps Places autocomplete and bind it to the input. Guarded so
  // it reuses an already-loaded script rather than injecting a second one.
  useEffect(() => {
    const initializeAutocomplete = () => {
      const input = document.getElementById('checkout-new-address');
      if (!input || !window.google) return;
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: 'au' }
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) return;
        const formattedAddress = { address_line1: '', city: '', state: '', postal_code: '' };
        for (let component of place.address_components) {
          const componentType = component.types[0];
          switch (componentType) {
            case 'street_number':
              formattedAddress.address_line1 = `${component.long_name} ${formattedAddress.address_line1}`;
              break;
            case 'route':
              formattedAddress.address_line1 += component.short_name;
              break;
            case 'postal_code':
              formattedAddress.postal_code = component.long_name;
              break;
            case 'locality':
              formattedAddress.city = component.long_name;
              break;
            case 'administrative_area_level_1':
              formattedAddress.state = component.short_name;
              break;
            default:
              break;
          }
        }
        setAddressInput(place.formatted_address);
        setStructuredAddress(formattedAddress);
      });
    };

    if (!window.google) {
      const apiKey = window.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const existing = document.getElementById('google-maps-script');
      if (existing) {
        existing.addEventListener('load', initializeAutocomplete);
        return () => existing.removeEventListener('load', initializeAutocomplete);
      }
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initializeAutocomplete;
      document.body.appendChild(script);
    } else {
      initializeAutocomplete();
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user found');

      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('deb_id')
        .eq('id', user.id)
        .single();

      if (accountError) throw accountError;
      if (!accountData.deb_id) throw new Error('No company deb_id found for this account');

      const addressData = (structuredAddress && structuredAddress.postal_code)
        ? structuredAddress
        : parseManualAddress(addressInput);

      if (!addressData.postal_code) {
        throw new Error('Please enter a valid address with a postal code');
      }

      const { data: inserted, error } = await supabase
        .from('delivery_addresses')
        .insert({
          account_id: user.id,
          company_deb_id: accountData.deb_id,
          created_by: user.id,
          address_line1: addressData.address_line1,
          city: addressData.city,
          state: addressData.state,
          postal_code: addressData.postal_code,
          country: 'Australia',
          is_primary: false
        })
        .select()
        .single();

      if (error) throw error;

      showNotification('Address added successfully and shared with all company users', 'success');
      onAdded(inserted);
    } catch (error) {
      console.error('Error adding address:', error);
      showError(error, 'Failed to add address');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Add Delivery Address
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors duration-200"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              id="checkout-new-address"
              type="text"
              value={addressInput}
              onChange={(e) => { setAddressInput(e.target.value); setStructuredAddress(null); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
              placeholder="Start typing your address..."
              autoComplete="off"
            />
            <p className="text-xs text-gray-500 mt-1">
              Select from suggestions, or type as: Street Address, City, State Postcode
            </p>
          </div>
          <p className="text-xs text-gray-500">
            This address will be shared with all users at your company.
          </p>
        </div>

        <div className="flex justify-end space-x-2 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !addressInput.trim()}
            className={`px-4 py-2 rounded-lg font-medium flex items-center transition-colors duration-200 ${
              saving || !addressInput.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-brand text-brand-dark hover:bg-brand-hover'
            }`}
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" />
                Add Address
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const UserCheckout = () => {
  const { enabled: orderingEnabled } = useOrderingEnabled();
  const [cartItems, setCartItems] = useState([]);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [allowPartialDelivery, setAllowPartialDelivery] = useState(true);
  const [orderInstructions, setOrderInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Review, 2: Details, 3: Confirm
  const [processingStep, setProcessingStep] = useState(0);
  const [processingSteps, setProcessingSteps] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const history = useHistory();

  useEffect(() => {
    fetchDeliveryAddresses();
    const storedCart = localStorage.getItem('cart');
    if (storedCart) {
      const parsedCart = JSON.parse(storedCart);
      setCartItems(Array.isArray(parsedCart) ? parsedCart : []);
    }
  }, []);

  const fetchDeliveryAddresses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // First get the user's deb_id (company identifier)
        const { data: accountData, error: accountError } = await supabase
          .from('accounts')
          .select('deb_id')
          .eq('id', user.id)
          .single();

        if (accountError) throw accountError;
        if (!accountData.deb_id) throw new Error('No company deb_id found for this account');

        // Fetch all delivery addresses for this company
        const { data, error } = await supabase
          .from('delivery_addresses')
          .select('*')
          .eq('company_deb_id', accountData.deb_id)
          .order('is_primary', { ascending: false });

        if (error) throw error;
        setDeliveryAddresses(data);
        if (data.length > 0) {
          setSelectedAddressId(data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching delivery addresses:', error);
      showError(error, 'Failed to fetch delivery addresses');
    }
  };

  const handleAddressAdded = (newAddress) => {
    setDeliveryAddresses(prev => [...prev, newAddress]);
    setSelectedAddressId(newAddress.id);
    setShowAddressModal(false);
  };

  const updateItemComment = (index, comment) => {
    const updatedItems = [...cartItems];
    updatedItems[index].comment = comment;
    setCartItems(updatedItems);
  };

  const removeItem = (index) => {
    const updatedItems = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedItems);
    localStorage.setItem('cart', JSON.stringify(updatedItems));
    notifyCartUpdated();
    showNotification('Item removed from order', 'success');
  };

  const validateStockAvailability = async (cartItems) => {
    // Check a single line's stock. Fails safe: any error/non-OK response
    // treats the whole line as backordered rather than silently in-stock.
    const validateItem = async (item) => {
      try {
        const response = await fetch('/api/enquire/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            partNumber: item.partNumber,
            account_id: localStorage.getItem('accountId')
          }),
        });

        if (!response.ok) {
          console.warn(`Could not fetch stock for ${item.partNumber}, assuming 0 available`);
          return {
            ...item,
            availableQty: 0,
            backorderQty: item.quantity,
            hasBackorder: true
          };
        }

        const stockData = await response.json();
        const availableQty = stockData.qty_available || 0;
        const backorderQty = Math.max(0, item.quantity - availableQty);

        return {
          ...item,
          availableQty: Math.min(item.quantity, availableQty),
          backorderQty,
          hasBackorder: backorderQty > 0
        };
      } catch (error) {
        console.error(`Error checking stock for ${item.partNumber}:`, error);
        return {
          ...item,
          availableQty: 0,
          backorderQty: item.quantity,
          hasBackorder: true
        };
      }
    };

    // Validate in batches to keep large orders responsive without firing
    // hundreds of simultaneous requests (which previously forced a >50-item
    // skip that silently recorded zero backorders on bulk orders).
    const BATCH_SIZE = 25;
    const results = [];
    for (let i = 0; i < cartItems.length; i += BATCH_SIZE) {
      const batch = cartItems.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(batch.map(validateItem));
      results.push(...batchResults);
    }
    return results;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Re-check the global ordering switch right before submitting
    const ordering = await fetchOrderingSettings();
    if (!ordering.enabled) {
      showNotification(ordering.message, 'error');
      return;
    }

    setLoading(true);
    setProcessingStep(0);
  
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user found');
      
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('deb_id, company_name, full_name, id')
        .eq('id', user.id)
        .single();
      
      if (accountError) throw accountError;
      if (!accountData.deb_id) throw new Error('Missing debtor ID for this account');

      // Define processing steps based on order size
      const steps = cartItems.length > 50 
        ? ['Submitting order...', 'Sending confirmation email...', 'Notifying staff...', 'Finalizing...']
        : ['Validating stock...', 'Submitting order...', 'Sending confirmation email...', 'Notifying staff...', 'Finalizing...'];
      
      setProcessingSteps(steps);

      // Step 1: Validate stock (skip for bulk orders)
      if (cartItems.length <= 50) {
        setProcessingStep(0);
        console.log('Validating stock availability...');
      }
      const validatedItems = await validateStockAvailability(cartItems);
      
      const backorderItems = validatedItems.filter(item => item.hasBackorder);
      const hasBackorders = backorderItems.length > 0;
      
      if (hasBackorders) {
        console.log(`Order has ${backorderItems.length} items with backorders`);
        // Add backorder sync step if needed
        if (!steps.includes('Syncing backorders...')) {
          steps.splice(steps.length - 1, 0, 'Syncing backorders...');
          setProcessingSteps([...steps]);
        }
      }

      // Step 2: Submit order
      setProcessingStep(cartItems.length > 50 ? 0 : 1);
      
      // Generate a professional order ID using database sequence
      const { data: orderIdData, error: orderIdError } = await supabase
        .rpc('generate_order_id');

      if (orderIdError) {
        throw new Error('Failed to generate order ID: ' + orderIdError.message);
      }

      const uniqueOrderId = orderIdData;
      console.log('Generated order ID:', uniqueOrderId);
      
      const { error: orderError } = await supabase
        .from('orders')
        .insert([
          { 
            user_id: user.id,
            order_id: uniqueOrderId,
            order_number: orderNumber,
            delivery_address_id: selectedAddressId,
            allow_partial_delivery: allowPartialDelivery,
            order_instructions: orderInstructions,
            cart_items: validatedItems,
            status: 'Processing',
            internal_status: 'pending_approval',
            has_backorders: hasBackorders,
            backorder_items: hasBackorders ? backorderItems.map(item => ({
              partNumber: item.partNumber,
              description: item.description,
              backorderQty: item.backorderQty
            })) : [],
            xml_generated_at: null
          }
        ]);
  
      if (orderError && orderError.code !== '23505') {
        throw orderError;
      }

      // Step 3: Send customer email
      setProcessingStep(cartItems.length > 50 ? 1 : 2);
      const sendCustomerEmailWithRetry = async (retries = 3) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            const { data: emailData, error: emailError } = await supabase.functions.invoke('send-order-email', {
              body: { orderId: uniqueOrderId }
            });

            if (emailError) {
              if (attempt === retries) {
                showNotification('Order submitted successfully, but confirmation email could not be sent', 'warning');
              }
            } else {
              console.log('Customer order confirmation email sent successfully');
              return true;
            }
          } catch (emailException) {
            if (attempt === retries) {
              showNotification('Order submitted successfully, but confirmation email could not be sent', 'warning');
            }
          }
          
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }
        }
        return false;
      };

      await sendCustomerEmailWithRetry();

      // Step 4: Send staff notification
      setProcessingStep(cartItems.length > 50 ? 2 : 3);
      const sendStaffNotificationWithRetry = async (retries = 3) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            await supabase.functions.invoke('send-staff-notification', {
              body: { orderId: uniqueOrderId }
            });
            console.log('Staff notification email sent successfully');
            return true;
          } catch (staffEmailException) {
            if (attempt === retries) {
              console.error('Staff notification failed after all retries');
            }
          }
          
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }
        }
        return false;
      };

      await sendStaffNotificationWithRetry();

      // Step 5: Sync backorders (if applicable)
      if (hasBackorders) {
        setProcessingStep(steps.length - 2); // Second to last step
        try {
          await supabase.functions.invoke('sync-backorders', {
            body: { 
              orderId: uniqueOrderId,
              accountData: {
                company_name: accountData.company_name || accountData.full_name,
                account_id: accountData.id
              },
              backorderItems: backorderItems.map(item => ({
                account: accountData.company_name || accountData.full_name,
                accountId: accountData.id,
                partNumber: item.partNumber,
                description: item.description,
                qty: item.backorderQty
              }))
            }
          });
          console.log('Backorders synced successfully to Google Sheets');
        } catch (syncException) {
          console.error('Exception syncing backorders:', syncException);
        }
      }
  
      // Final step
      setProcessingStep(steps.length - 1);
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause to show completion
      
      showNotification('Order submitted successfully and is being processed', 'success');
      localStorage.removeItem('cart');
      notifyCartUpdated();
      history.push('/user/dashboard');
    } catch (error) {
      console.error('Error processing order:', error);
      showError(error, 'Failed to process your order. Please try again.');
    } finally {
      setLoading(false);
      setProcessingStep(0);
      setProcessingSteps([]);
    }
  };

  const orderTotal = cartItems.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * item.quantity), 0);
  const selectedAddress = deliveryAddresses.find(addr => addr.id === selectedAddressId);

  if (cartItems.length === 0) {
    return (
      <UserLayout>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Checkout</h1>
            <nav className="text-sm text-gray-600 mt-1">
              <button 
                onClick={() => history.push('/user/dashboard')}
                className="hover:text-brand transition-colors duration-200"
              >
                Dashboard
              </button>
              <span className="mx-2">{'>'}</span>
              <span>Checkout</span>
            </nav>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
            <p className="text-gray-600 mb-6">Add items to your cart before proceeding to checkout</p>
            <button
              onClick={() => history.push('/user/part-enquiry')}
              className="inline-flex items-center px-6 py-3 bg-brand text-brand-dark rounded-lg font-semibold hover:bg-brand-hover transition-colors duration-200"
            >
              <Package className="w-5 h-5 mr-2" />
              Browse Parts
            </button>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      {/* Processing Overlay */}
      <AnimatePresence>
        {loading && processingSteps.length > 0 && (
          <ProcessingOverlay steps={processingSteps} currentStep={processingStep} />
        )}
      </AnimatePresence>

      {/* Add Address Modal */}
      <AnimatePresence>
        {showAddressModal && (
          <AddAddressModal
            onClose={() => setShowAddressModal(false)}
            onAdded={handleAddressAdded}
          />
        )}
      </AnimatePresence>

      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Checkout</h1>
            <nav className="text-sm text-gray-600 mt-1">
              <button 
                onClick={() => history.push('/user/dashboard')}
                className="hover:text-brand transition-colors duration-200"
              >
                Dashboard
              </button>
              <span className="mx-2">{'>'}</span>
              <span>Checkout</span>
            </nav>
          </div>
          <div className="mt-4 sm:mt-0">
            <span className="text-sm text-gray-600">
              {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} • Total: {orderTotal > 0 ? `$${orderTotal.toFixed(2)}` : 'POA'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Summary - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Package className="w-5 h-5 mr-2" />
                Order Summary
              </h2>
              
              <AnimatePresence>
                {cartItems.map((item, index) => (
                  <motion.div
                    key={index}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <OrderSummaryItem
                      item={item}
                      index={index}
                      onUpdateComment={updateItemComment}
                      onRemoveItem={removeItem}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Order Total */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Order Total:</span>
                  <span className="text-2xl font-bold text-green-600">
                    {orderTotal > 0 ? `$${orderTotal.toFixed(2)}` : 'Price on Application'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  *Final pricing will be confirmed in your quote
                </p>
              </div>
            </div>
          </div>

          {/* Checkout Form - Right Column */}
          <div className="space-y-6">
            {/* Order Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Order Details
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Number
                  </label>
                  <input
                    type="text"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    placeholder="Enter your order number"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Address
                  </label>
                  <div className="space-y-2">
                    {deliveryAddresses.map(address => (
                      <AddressCard
                        key={address.id}
                        address={address}
                        isSelected={selectedAddressId === address.id}
                        onSelect={setSelectedAddressId}
                      />
                    ))}
                  </div>
                  {deliveryAddresses.length === 0 && (
                    <p className="text-sm text-gray-500">
                      No delivery addresses on file. Add one to continue.
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowAddressModal(true)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add New Address
                  </button>
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={allowPartialDelivery}
                      onChange={(e) => setAllowPartialDelivery(e.target.checked)}
                      className="form-checkbox h-4 w-4 text-brand rounded border-gray-300 focus:ring-brand"
                    />
                    <span className="text-sm font-medium text-gray-700">Allow Partial Delivery</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Receive available items first, backorders sent separately
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Instructions
                  </label>
                  <textarea
                    value={orderInstructions}
                    onChange={(e) => setOrderInstructions(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                    rows="3"
                    placeholder="Any special delivery or handling instructions..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {orderInstructions.length}/200 characters
                  </p>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading || !selectedAddressId || !orderNumber || !orderingEnabled}
                    className={`w-full flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                      loading || !selectedAddressId || !orderNumber || !orderingEnabled
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-brand text-brand-dark hover:bg-brand-hover'
                    }`}
                  >
                    {loading ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="mr-3"
                        >
                          <Loader className="w-5 h-5" />
                        </motion.div>
                        Processing Order...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Place Order
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Order Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800 mb-1">Order Processing</p>
                  <p className="text-blue-700">
                    Your order will be reviewed by our team and processed within 1-2 business days. You'll receive confirmation and tracking information via email.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UserLayout>
  );
};

export default UserCheckout;
